import React from "react";
import PropTypes from "prop-types";
import { Card, CardMedia, CardContent, Typography, Box } from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { Link } from "react-router-dom";

// Helper function to get the ordinal suffix for the day
const getOrdinal = (day) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = day % 100;
  return `${day}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
};

// Helper function to format the date
const formatDate = (dateString) => {
  const date = new Date(dateString);

  const month = date.toLocaleString("default", { month: "long" });
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  const isAM = hours < 12;
  const hour12 = hours % 12 || 12;
  const formattedTime = `${hour12}:${minutes < 10 ? "0" + minutes : minutes} ${
    isAM ? "AM" : "PM"
  }`;

  return `${month} ${getOrdinal(day)}, ${year} - ${formattedTime}`;
};

// Decide event status (upcoming, ongoing, past)
const getEventStatus = (event) => {
  const now = new Date();
  const start = new Date(event.start_date_time);
  const end = event.end_date_time ? new Date(event.end_date_time) : null;

  if (start > now) return "upcoming";
  if (end && start <= now && end >= now) return "ongoing";
  if (!end && start.toDateString() === now.toDateString()) return "ongoing";
  return "past";
};

function EventCard({ event }) {
  const status = getEventStatus(event);

  return (
    <Box sx={{ px: 1 }} className="event-lists">
      <Link className="link" to={`/events/${event.slug}`} style={{ textDecoration: "none" }}>
        <Card
          className={`theme-radius event-card ${status}-ribbon`}
          sx={{ position: "relative", overflow: "hidden" }}
        >
          {/* Ribbon */}
          <Box className={`ribbon ribbon-${status}`}>
            {status === "upcoming" && "Upcoming"}
            {status === "ongoing" && "Ongoing"}
            {status === "past" && "Past Event"}
          </Box>

          <CardMedia
            className="theme-radius event-img"
            component="img"
            alt={event.title}
            height="200"
            image={event.image}
          />
          <CardContent sx={{ pb: 4 }}>
            <Typography
              gutterBottom
              variant="h6"
              component="h3"
              dangerouslySetInnerHTML={{ __html: event.title }}
              sx={{ mb: 2 }}
            />

            {event.start_date_time && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <DateRangeIcon fontSize="small" sx={{ mr: 0.5 }} />
                Start Date: {formatDate(event.start_date_time)}
              </Typography>
            )}

            {event.end_date_time && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <DateRangeIcon fontSize="small" sx={{ mr: 0.5 }} />
                End Date: {formatDate(event.end_date_time)}
              </Typography>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
              Read More
            </Box>
          </CardContent>
        </Card>
      </Link>
    </Box>
  );
}

EventCard.propTypes = {
  event: PropTypes.shape({
    title: PropTypes.string.isRequired,
    start_date_time: PropTypes.string.isRequired,
    end_date_time: PropTypes.string,
    image: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    category_name: PropTypes.string,
  }).isRequired,
};

export default EventCard;
